import React from 'react';
import { createPortal } from 'react-dom';
import { C } from '../theme.js';

// ── Modal — shared full-viewport dialog ─────────────────────────────────
// Blurred-backdrop overlay in the same style as the file browser modals,
// layered above the floating inspector (2000) and the file browsers
// (2100/2110) but under toasts (2200).
//
//   onClose      called for Escape, the backdrop (only when !dirty) and — via
//                the caller's own buttons — anything else. The caller decides
//                what "close" means (e.g. confirm when there is unsaved text).
//   dirty        true while there is unsaved text: a backdrop click is then
//                ignored (Escape still calls onClose, so the caller can ask).
//
// Escape is handled in the capture phase and stopped, so App-level key
// handlers (fullscreen exit, column-highlight clear) don't also fire. Body
// scroll is locked while open, Tab is trapped inside the dialog, and focus
// returns to whatever had it before opening.
const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({ onClose, dirty = false, label, children, width = 'min(1180px, 100%)', height = 'min(740px, 100%)', zIndex = 2150 }) {
  const dialogRef = React.useRef(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  // Scroll lock + focus restore.
  React.useEffect(() => {
    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const first = dialogRef.current?.querySelector('[data-autofocus]') || dialogRef.current?.querySelector(FOCUSABLE);
    (first || dialogRef.current)?.focus?.();
    return () => {
      document.body.style.overflow = prevOverflow;
      if (prevFocus && typeof prevFocus.focus === 'function' && document.contains(prevFocus)) prevFocus.focus();
    };
  }, []);

  // Escape (stopped) + Tab trap.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const nodes = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE)).filter(n => n.offsetParent !== null || n === document.activeElement);
      if (!nodes.length) { e.preventDefault(); return; }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (!dialogRef.current.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget && !dirty) onCloseRef.current?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex,
        background: 'rgba(15, 17, 23, 0.6)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        style={{
          width, height, background: C.app,
          border: `1px solid ${C.borderStrong}`, borderRadius: 12,
          boxShadow: '0 24px 64px rgba(0,0,0,.6)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', outline: 'none',
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
